import React, { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Row, Col, Card } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { fetchJson } from "../utils/fetchJson";
import { findJournal } from "../utils/formatJournalPath";

const GuidePage = () => {
  const { journalId } = useParams();
  const [guideData, setGuideData] = useState([]);
  const [journalTitle, setJournalTitle] = useState("");
  const [loadError, setLoadError] = useState("");
  const [expandedItems, setExpandedItems] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const sidebarRef = useRef(null);
  const generateHashFromTitle = (title) => {
    return title
      .replace(/[^\w\s]/g, "") // 移除非字母数字和空格的字符
      .replace(/\s+/g, "-") // 空格换-
      .toLowerCase(); // 全部小写
  };

  // 监听屏幕尺寸，判断是否为移动端（768px为Bootstrap默认md断点）
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    fetchGuide();
  }, [journalId]);

  const loadGuideJson = (path) => {
    return fetchJson(path).then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load ${path}`);
      }
      return response.json();
    });
  };

  const fetchGuide = () => {
    setGuideData([]);
    setJournalTitle("");
    setLoadError("");
    setExpandedItems({});

    if (!journalId) {
      loadGuideJson("/json/guidelines/common.json")
        .then((data) => setGuideData(data || []))
        .catch((error) => {
          console.error("Error loading common:", error);
          setLoadError("Unable to load Author Guidelines.");
        });
      return;
    }

    fetchJson("/json/journals.json")
      .then((response) => response.json())
      .then((data) => {
        const journal = findJournal(data.journals, journalId);
        if (!journal || !journal.has_guide) {
          setLoadError("This journal does not have dedicated Author Guidelines yet.");
          return;
        }
        setJournalTitle(journal.title);
        return loadGuideJson(`/json/guidelines/${journal.id}.json`).then(
          (guide) => setGuideData(guide || [])
        );
      })
      .catch((error) => {
        console.error("Error loading journal guidelines:", error);
        setLoadError("Unable to load Author Guidelines for this journal.");
      });
  };

  const toggleExpand = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };
  const scrollWithOffset = (element) => {
    const headerOffset = 100; // 你的导航栏高度，必要时调整
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const handleItemClick = (title, e) => {
    e.preventDefault();

    const hash = generateHashFromTitle(title);
    const targetEl = document.getElementById(hash);

    // 更新地址栏 hash（不会触发默认跳转）
    window.history.replaceState(null, "", `#${hash}`);

    if (targetEl) scrollWithOffset(targetEl);

    if (isMobile) {
      setExpandedItems({});
    }
  };

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) scrollWithOffset(el);
      }, 200); // 等内容渲染完成
    }
  }, [guideData]);

  // 递归渲染菜单
  const renderMenuItems = (items, level = 0) => {
    return items
      .filter((item) => item.inNav !== false)
      .map((item) => {
        const navChildren = (item.subsections || []).filter(
          (child) => child.inNav !== false
        );
        return (
      <li key={item.id} className={`mb-2 ${level > 0 ? "ms-3" : ""}`}>
        <a
          href={`#${generateHashFromTitle(item.title)}`}
          onClick={(e) => handleItemClick(item.title, e)}
          className="btn d-flex justify-content-between align-items-center btn-secondary"
          style={{
            textAlign: "left",
            padding: isMobile ? "6px 12px" : "8px 15px",
            borderRadius: "8px",
            transition: "all 0.3s ease",
            width: "100%",
            fontSize: isMobile ? "0.9rem" : "1rem",
          }}
        >
          <span
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flexGrow: 1,
              marginRight: "8px",
            }}
          >
            {item.title}
          </span>

          {navChildren.length > 0 && (
            <FontAwesomeIcon
              icon={expandedItems[item.id] ? faChevronDown : faChevronRight}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation(); // 阻止事件冒泡到父元素
                e.preventDefault(); // 阻止默认链接行为
                toggleExpand(item.id);
              }}
            />
          )}
        </a>

        {navChildren.length > 0 && expandedItems[item.id] && (
            <ul
              style={{
                listStyle: "none",
                padding: "5px 0 0 0",
                marginTop: "5px",
              }}
            >
              {renderMenuItems(item.subsections, level + 1)}
            </ul>
          )}
      </li>
        );
      });
  };

  // 递归渲染内容（保持不变）
  const renderContentItems = (items, level = 0) => {
    return items.map((item) => {
      const isSubheading = item.inNav === false;
      return (
      <React.Fragment key={`${item.id}-${item.title}`}>
        <section
          id={generateHashFromTitle(item.title)}
          className="card mb-4 p-4"
          style={{ marginBottom: isMobile ? "1.5rem" : "1rem" }}
        >
          <h2
            className="mb-3"
            style={{
              color: "#2c3e50",
              fontSize:
                level === 0
                  ? isMobile
                    ? "1.5rem"
                    : "1.8rem"
                  : isMobile
                    ? "1.3rem"
                    : "1.5rem",
              borderLeft: isSubheading ? "none" : `4px solid ${level === 0 ? "#3498db" : "#2980b9"}`,
              paddingLeft: isSubheading ? "0px" : "15px",
              marginBottom: isMobile ? "1rem" : "0.5rem",
            }}
          >
            {item.title}
          </h2>

          {item.table ? (
            <div
              className="mt-3 overflow-x-auto"
              style={{
                minWidth: "320px",
                marginBottom: isMobile ? "1rem" : "0.5rem",
              }}
            >
              <table className="table table-bordered w-100">
                <thead className="text-center bg-light">
                  <tr>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Article Type
                    </th>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Description
                    </th>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Length
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {item.table.map((row, idx) => (
                    <tr key={idx}>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.type}
                      </td>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.desc}
                      </td>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.length}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              style={{
                lineHeight: "1.8",
                color: "#555",
                fontSize: isMobile ? "0.95rem" : "1.05rem",
                textAlign: isMobile ? "" : "justify",
                wordSpacing: "2px",
                marginBottom: isMobile ? "1rem" : "0.5rem",
              }}
              dangerouslySetInnerHTML={{
                __html: (item.content || "").replace(/\n/g, "<br>"),
              }}
            />
          )}

          {item.table_apc ? (
            <div className="mt-3 overflow-x-auto" style={{ minWidth: "320px" }}>
              <table className="table table-bordered w-100 text-center">
                <thead className="bg-light">
                  <tr>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Journal Name
                    </th>
                    <th style={{ whiteSpace: "nowrap", padding: "0.5rem" }}>
                      Article Publishing Charge, APC
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {item.table_apc.map((row, idx) => (
                    <tr key={idx}>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.name}
                      </td>
                      <td
                        style={{
                          wordWrap: "break-word",
                          padding: "0.5rem",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {row.article}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            ""
          )}

          {item.details &&
            item.details.map((detail, detailIndex) => (
              <div key={detailIndex}>
                <div
                  style={{
                    fontSize: isMobile ? "0.95rem" : "1rem",
                    textAlign: isMobile ? "" : "justify",
                    wordSpacing: "2px",
                    color: "#555",
                  }}
                >

                  {detail.title && (
                    <div
                      style={{
                        fontSize: isMobile ? "0.95rem" : "1rem",
                        textAlign: isMobile ? "" : "justify",
                        wordSpacing: "2px",
                        color: "#555",
                      }}
                      dangerouslySetInnerHTML={{ __html: detail.title }}
                    />
                  )}
                </div>

                <ul
                  style={{
                    listStyleType: "disc",
                    paddingLeft: isMobile ? "20px" : "30px",
                    lineHeight: "1.8",
                    color: "#555",
                    fontSize: isMobile ? "0.95rem" : "1rem",
                  }}
                >
                  {detail.content &&
                    detail.content.map((contentItem, contentIndex) => (
                      <>
                        <li
                          key={contentIndex}
                          style={{
                            marginBottom: isMobile ? "0px" : "0px",
                            textAlign: isMobile ? "" : "justify",
                          }}
                          dangerouslySetInnerHTML={{ __html: contentItem.title }}
                        />
                        <div dangerouslySetInnerHTML={{ __html: contentItem.link }} />
                      </>
                    ))}
                </ul>
              </div>
            ))}
        </section>

        {item.subsections &&
          item.subsections.length > 0 &&
          renderContentItems(item.subsections, level + 1)}
      </React.Fragment>
      );
    });
  };

  return (
    <div>
      <div className="page-title">
        <h1>{journalTitle ? journalTitle : "Author Guidelines"}</h1>
      </div>

      {loadError ? (
        <div className="text-center py-5">
          <p className="text-muted">{loadError}</p>
          <Link to="/guide">View publisher Author Guidelines</Link>
        </div>
      ) : (
        <Row gap={4} style={{ position: "relative" }}>
          <Col xs={12} md={3}>
            <Card
              ref={sidebarRef}
              style={{
                maxHeight: isMobile
                  ? "calc(100vh - 40px)"
                  : "calc(100vh - 100px)",
                overflowY: "auto",
                padding: isMobile ? "1rem" : "1.5rem",
                position: isMobile ? "static" : "sticky",
                top: isMobile ? "auto" : "100px",
                left: isMobile ? "auto" : 0,
                zIndex: 10,
              }}
            >
              <Card.Header
                style={{
                  padding: 0,
                  borderBottom: "2px solid #3498db",
                  marginBottom: "1rem",
                  backgroundColor: "transparent",
                }}
              >
                <h3
                  style={{
                    color: "#2c3e50",
                    paddingBottom: "10px",
                    margin: 0,
                    fontSize: isMobile ? "1.2rem" : "1.3rem",
                  }}
                >
                  Content Overview
                </h3>
              </Card.Header>
              <Card.Body style={{ padding: 0 }}>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {renderMenuItems(guideData)}
                </ul>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={9}>
            <main>
              {guideData.length > 0 ? (
                renderContentItems(guideData)
              ) : (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "300px" }}
                >
                  <div className="text-center">
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p>Loading content</p>
                  </div>
                </div>
              )}
            </main>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default GuidePage;
